import dayTextureUrl from "@assets/textures/day.jpg";
import { OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import React, { Suspense, useRef, useState, useMemo } from "react";
import * as THREE from "three";
import styles from "./Earth.module.scss";
import { getAdaptiveSettings, THREEJS_CONFIG } from "../../lib/performance";

// Earth mesh component with texture preloading
const EarthMesh = React.memo(
  ({
    enableAutoRotation = true,
    segments = 32,
  }: {
    enableAutoRotation?: boolean;
    segments?: number;
  }) => {
    const earthRef = useRef<THREE.Mesh>(null);
    const earthTexture = useLoader(THREE.TextureLoader, dayTextureUrl);

    if (earthTexture) {
      earthTexture.colorSpace = THREE.SRGBColorSpace;
      earthTexture.minFilter = THREE.LinearFilter;
      earthTexture.magFilter = THREE.LinearFilter;
    }

    useFrame((_, delta) => {
      if (earthRef.current && enableAutoRotation) {
        // Use delta for smoother, frame-rate independent rotation
        earthRef.current.rotation.y += delta * 0.1;
      }
    });

    return (
      <mesh ref={earthRef} position={[0, 0.0, 0]}>
        <sphereGeometry args={[1.2, segments, segments]} />
        <meshStandardMaterial
          map={earthTexture}
          roughness={0.6}
          metalness={0.05}
          envMapIntensity={0.8}
        />
      </mesh>
    );
  }
);

// Loading placeholder component
const LoadingPlaceholder = () => (
  <mesh position={[-1.5, 0.5, 0]}>
    <sphereGeometry args={[1.2, 16, 16]} />
    <meshStandardMaterial
      color="#1a1a1a"
      roughness={0.8}
      metalness={0.2}
      wireframe
    />
  </mesh>
);

// Main Earth component
const Earth = React.memo(() => {
  const [isInteracting, setIsInteracting] = useState(false);
  const adaptiveSettings = useMemo(() => getAdaptiveSettings(), []);

  return (
    <div
      className={`${styles.earthContainer} earth-container`}
      role="img"
      aria-label="Interactive 3D Earth model - drag to rotate"
    >
      <Canvas
        className={`${styles.earthCanvas} earth-canvas`}
        gl={{
          ...THREEJS_CONFIG.CANVAS_SETTINGS,
          antialias: adaptiveSettings.enableAntialiasing,
          alpha: true,
        }}
        frameloop={adaptiveSettings.frameloop as "always" | "demand" | "never"}
        performance={THREEJS_CONFIG.PERFORMANCE_SETTINGS}
      >
        <PerspectiveCamera makeDefault position={[0, 0, 6]} fov={45} />
        <OrbitControls
          enablePan={false}
          enableZoom={false}
          enableRotate={true}
          enableDamping={true}
          dampingFactor={0.05}
          minDistance={3.8}
          maxDistance={5}
          onStart={() => setIsInteracting(true)}
          onEnd={() => setIsInteracting(false)}
        />
        <ambientLight intensity={2.0} />
        <directionalLight
          position={[2.5, 1, 1.5]}
          intensity={3.5}
          castShadow
        />
        <directionalLight position={[-1.5, 0, -2]} intensity={1.0} />
        <hemisphereLight args={["#ffffff", "#777777", 1.0]} />
        <Suspense fallback={<LoadingPlaceholder />}>
          <EarthMesh
            enableAutoRotation={!isInteracting}
            segments={adaptiveSettings.earthSegments}
          />
        </Suspense>
        {/* Add subtle glow effect when interacting */}
        {isInteracting && (
          <mesh position={[0, 0, 0]} scale={1.25}>
            <sphereGeometry args={[1.2, 32, 32]} />
            <meshBasicMaterial
              color="#4a90e2"
              transparent
              opacity={0.1}
              side={THREE.BackSide}
            />
          </mesh>
        )}
      </Canvas>
    </div>
  );
});

export default Earth;
